import { useState } from 'react';
import { Search, SlidersHorizontal, Check, AlertTriangle, PackageOpen, SearchX } from 'lucide-react';
import { EmptyState, SkeletonOrderCard, PullToRefresh } from '../../components/boothly/index.js';
import { fmtCurrency } from '../../lib/format.js';
import { Portal } from '../../components/boothly/portal.jsx';
import { ActionButton } from '../../action-button.jsx';
import { useAnySheetOpen } from '../../widgets.jsx';

// ── Urgency helpers ───────────────────────────────────────────────────────────

function ageHours(dateStr) {
  if (!dateStr) return 0;
  const then = new Date(dateStr);
  const now = new Date();
  return (now - then) / (1000 * 3600);
}

function hasUrgency(order) {
  const hours = ageHours(order.datum);
  const status = order.status;
  return (status === 'Offen' || status === 'Bereit') && hours >= 12;
}

// ── Order Card ────────────────────────────────────────────────────────────────

function OrderCard({ order, onClick, selectMode, selected, getChannelLabel }) {
  const statusClass = {
    Offen:     'status-offen',
    Bereit:    'status-bereit',
    Versendet: 'status-versendet',
    Retoure:   'status-retoure',
  }[order.status] ?? 'status-offen';

  const displayNum    = order.bestellnummer || order.id;
  const channelLabel  = getChannelLabel?.(order.channelId) || order.kanal || '–';
  // testid bleibt namens-slug-basiert (nicht die channel_id-UUID) — REG-006-Guard.
  const channelSlug   = (channelLabel || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'na';
  const dateFormatted = order.datum
    ? new Date(order.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
    : '–';
  const price = order.preis != null ? fmtCurrency(order.preis) : '–';
  const showAlert = hasUrgency(order);

  return (
    <button
      onClick={onClick}
      data-testid={showAlert ? 'order-card-overdue' : 'order-card'}
      data-overdue={showAlert ? 'true' : 'false'}
      className={`glass-card glass-shine w-full rounded-2xl p-3 text-left transition-all active:scale-[0.98]${showAlert ? ' order-card-overdue' : ''}${selected ? ' ring-2 ring-ios-blue' : ''}`}
    >
      <div className="flex items-center gap-3">
        {selectMode && (
          <div
            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
              selected ? 'border-ios-blue bg-ios-blue' : 'border-muted-foreground'
            }`}
          >
            {selected && <Check className="h-3 w-3 text-white" />}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Row 1: Bestellnummer (truncated) + status pill */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold truncate max-w-[65%]">
              {showAlert && <AlertTriangle className="inline h-3 w-3 mr-1" style={{ color: 'var(--accent-yellow)' }} />}
              #{displayNum}
            </span>
            <span data-testid={`status-pill-${order.status.toLowerCase()}`} className={`status-pill ${statusClass} flex-shrink-0`}>
              {order.status}
            </span>
          </div>

          {/* Row 2: Price · via channel · date (single muted line) */}
          <p className="text-xs text-muted-foreground truncate">
            {price} · via <span data-testid={`channel-tag-${channelSlug}`}>{channelLabel}</span> · {dateFormatted}
          </p>
        </div>
      </div>
    </button>
  );
}

// ── Bestellungen ──────────────────────────────────────────────────────────────

export function Bestellungen({ store, currentRole, dark, onRefresh }) {
  const isHelper = store.helperMode;
  const [activeFilter, setActiveFilter] = useState(isHelper ? 'bereit' : 'alle');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [selectMode,   setSelectMode]   = useState(false);
  const [selectedIds,  setSelectedIds]  = useState([]);

  const orders = store.orders ?? [];
  const isLoading = !store.loaded;

  const canWrite = currentRole !== 'viewer' && currentRole !== 'finance';
  const anySheetOpen = useAnySheetOpen();

  // "Kürzlich versendet" = letzte 7 Tage
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recentlyShipped = orders.filter(o => {
    if (o.status !== 'Versendet') return false;
    const ts = o.updatedAt ? new Date(o.updatedAt).getTime() : (o.datum ? new Date(o.datum).getTime() : 0);
    return ts > sevenDaysAgo;
  });

  // Filter chip definitions — Helfer sieht nur Bereit + Kürzlich versendet
  const filters = isHelper
    ? [
        { id: 'bereit',              label: 'Bereit',             count: store.counts?.Bereit ?? orders.filter(o => o.status === 'Bereit').length },
        { id: 'kuerzlich-versendet', label: 'Kürzlich versendet', count: recentlyShipped.length },
      ]
    : [
        { id: 'alle',      label: 'Alle',      count: orders.length },
        { id: 'offen',     label: 'Offen',     count: store.counts?.Offen     ?? orders.filter(o => o.status === 'Offen').length },
        { id: 'bereit',    label: 'Bereit',    count: store.counts?.Bereit    ?? orders.filter(o => o.status === 'Bereit').length },
        { id: 'versendet', label: 'Versendet', count: store.counts?.Versendet ?? orders.filter(o => o.status === 'Versendet').length },
        { id: 'retoure',   label: 'Retoure',   count: store.counts?.Retoure   ?? orders.filter(o => o.status === 'Retoure').length },
      ];

  // Filtered list
  const filtered = (() => {
    // Helfer: "Kürzlich versendet" hat eigene Logik
    if (isHelper && activeFilter === 'kuerzlich-versendet') return recentlyShipped;

    return orders.filter(order => {
      if (activeFilter !== 'alle' && order.status.toLowerCase() !== activeFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const num = (order.bestellnummer || order.id || '').toLowerCase();
        const kanal = (store.getChannelLabel?.(order.channelId) || order.kanal || '').toLowerCase();
        return num.includes(q) || kanal.includes(q);
      }
      return true;
    });
  })();

  // Select toggle
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleOrderClick = (order) => {
    if (selectMode) {
      toggleSelect(order.id);
    } else {
      store.openDetail(order);
    }
  };

  // Batch actions
  const handleBatchAction = (status) => {
    selectedIds.forEach(id => store.updateOrder(id, 'status', status));
    setSelectedIds([]);
    setSelectMode(false);
  };

  return (
    <>
      <PullToRefresh onRefresh={onRefresh}>
        <div data-testid="screen-bestellungen" className="flex flex-col h-full px-5 pt-4">
        {/* Search Bar */}
        <div className="px-1 mb-4">
          <div className="glass-card flex items-center gap-3 rounded-xl px-4 py-3">
            <Search className="h-5 w-5 text-muted-foreground" />
            <input
              data-testid="search-bar"
              type="text"
              placeholder="Bestellung suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <button className="ghost-icon-btn -mr-2">
              <SlidersHorizontal className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="scrollable-x -mx-5 px-5 mb-4">
          <div className="flex gap-2">
            {filters.map(filter => (
              <button
                key={filter.id}
                data-testid={`filter-pill-${filter.id}`}
                onClick={() => setActiveFilter(filter.id)}
                className={`filter-chip whitespace-nowrap${activeFilter === filter.id ? ' active' : ''}`}
              >
                {filter.label}
                <span className="ml-1.5 opacity-70">{filter.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Count + Auswählen toggle */}
        <div className="flex items-center justify-between mb-3 px-1">
          <span className="text-sm text-muted-foreground">
            {isLoading ? '…' : `${filtered.length} Bestellung${filtered.length !== 1 ? 'en' : ''}`}
          </span>
          {canWrite && (
            <button
              onClick={() => { setSelectMode(!selectMode); setSelectedIds([]); }}
              className={`text-sm font-medium ${selectMode ? 'text-ios-blue' : 'text-muted-foreground'}`}
            >
              {selectMode ? `${selectedIds.length} ausgewählt` : 'Auswählen'}
            </button>
          )}
        </div>

        {/* Orders List */}
        <div className="flex-1 space-y-3" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonOrderCard key={i} />
            ))
          ) : orders.length === 0 ? (
            <EmptyState
              icon={PackageOpen}
              title="Noch keine Bestellungen"
              description="Sobald Bestellungen aus eBay, Etsy oder deinem Shop einlaufen, erscheinen sie hier."
              cta={!isHelper ? {
                label: 'Bestellung manuell anlegen',
                accent: 'online',
                onClick: () => store.openNew?.(),
              } : undefined}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title="Keine Treffer"
              description="Versuch's mit einem anderen Suchbegriff oder ändere den Filter."
              cta={{
                label: 'Filter zurücksetzen',
                accent: 'online',
                onClick: () => { setActiveFilter('alle'); setSearchQuery(''); },
              }}
            />
          ) : (
            filtered.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onClick={() => handleOrderClick(order)}
                selectMode={selectMode}
                selected={selectedIds.includes(order.id)}
                getChannelLabel={store.getChannelLabel}
              />
            ))
          )}
        </div>
        </div>
      </PullToRefresh>

      {/* Batch Action Bar — outside PullToRefresh to avoid transform inheritance */}
      {selectMode && selectedIds.length > 0 && (
        <div className="fixed inset-x-0 bottom-20 px-5 pb-safe z-40">
          <div className="glass-card flex items-center justify-between rounded-2xl p-4">
            <span className="font-medium">{selectedIds.length} ausgewählt</span>
            <div className="flex gap-2">
              <button
                onClick={() => { setSelectMode(false); setSelectedIds([]); }}
                className="rounded-xl bg-[var(--fill-tertiary)] px-4 py-2 text-sm font-medium"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action-Button — via Portal so position:fixed is relative to viewport, not SwipeableTabs transform */}
      {canWrite && !isHelper && !selectMode && !anySheetOpen && !store.showDetail && !store.showSheet && (
        <Portal>
          <ActionButton
            variant="floating"
            accent="var(--accent-online)"
            label="Neu"
            ariaLabel="Neue Bestellung anlegen"
            onClick={() => store.openNew?.()}
          />
        </Portal>
      )}
    </>
  );
}
